/**
 * `<UserActivityProvider>` + `useActivity()` — author-facing hook
 * for emitting activity events from anywhere in the LexIDE tree.
 *
 * The provider:
 *   - resolves the user/org identity via the existing
 *     `useAuthIdentity` hook (memoised + Sentry-tagged).
 *   - mints a per-tab `session_id` UUID on mount so every event
 *     from this tab clusters together in LexPortal.
 *   - exposes `track(category, type, payload, opts?)` — author API.
 *     Events go through PII redaction + payload truncation, then
 *     into the LocalStorage outbox where the flusher picks them up.
 *   - registers + drains a global error bridge so `Sentry.beforeSend`
 *     (called BEFORE React mounts) can buffer JS-error events that
 *     replay once the provider mounts.
 *
 * `track` no-ops cleanly until `useAuthIdentity()` resolves —
 * pre-auth events sit in a small in-memory buffer and replay once
 * the identity is known. The sequence preserves a click-then-load
 * UX without losing the click.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";

import type { ActivityEvent } from "@lexiun/lx-obs";

import { useAuthIdentity } from "@/hooks/useAuthIdentity";
import { useActivityStore } from "@/stores/useActivityStore";

import { redactPayload, truncateText } from "./redact";
import { drainErrorBuffer, setErrorListener, type ErrorBridgeEvent } from "./error-bridge";

const PROMPT_MAX_CHARS = 1_024;

export interface ActivityContextValue {
  /**
   * Emit an activity event. Filled in with cluster fields
   * (user_id, org_id, session_id, route, url, user_agent) by the
   * provider. Payload is PII-redacted before queuing.
   */
  track: (
    category: ActivityEvent["category"],
    type: string,
    payload?: Record<string, unknown>,
    opts?: TrackOpts,
  ) => void;
  /** Per-tab session id — exposed so deep components can pin events. */
  sessionId: string;
}

export interface TrackOpts {
  /** Override `case_id` cluster field for this event. */
  case_id?: string;
  /** Override `matter_id` cluster field for this event. */
  matter_id?: string;
  /** Override `client_id` cluster field for this event. */
  client_id?: string;
  /** Override `agent_id` cluster field for this event. */
  agent_id?: string;
  /** Override `route` (defaults to `window.location.pathname`). */
  route?: string;
}

const ActivityContext = createContext<ActivityContextValue | null>(null);

const CLIENT_VERSION =
  (import.meta.env.VITE_APP_VERSION as string | undefined) ??
  (import.meta.env.MODE as string | undefined) ??
  "dev";

export function UserActivityProvider({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  const { identity } = useAuthIdentity();
  const enqueue = useActivityStore((s) => s.enqueue);
  // Per-tab session id stays stable across renders. crypto.randomUUID
  // is universally available in Vite-supported targets.
  const sessionIdRef = useRef<string>("");
  if (!sessionIdRef.current) {
    sessionIdRef.current = crypto.randomUUID();
  }

  // Pre-auth buffer — events emitted before useAuthIdentity resolves
  // sit here and replay once we have a user_id.
  const pendingRef = useRef<ActivityEvent[]>([]);

  const buildEvent = useCallback(
    (
      category: ActivityEvent["category"],
      type: string,
      payload: Record<string, unknown> = {},
      opts: TrackOpts = {},
    ): ActivityEvent | null => {
      // PII scrub before enqueue (defense in depth — server scrubs
      // again before persistence).
      const { payload: redacted } = redactPayload(payload);
      // identity may be null — for pre-auth events we use a sentinel
      // user_id; the runtime endpoint overrides it with JWT sub
      // anyway, so the value is just bookkeeping until resolved.
      const userId = identity?.userId ?? "pre-auth";
      const event: ActivityEvent = {
        id: crypto.randomUUID(),
        ts: Date.now(),
        user_id: userId,
        org_id: identity?.orgId,
        session_id: sessionIdRef.current,
        case_id: opts.case_id,
        matter_id: opts.matter_id,
        client_id: opts.client_id,
        agent_id: opts.agent_id,
        source: "lexide",
        subsystem: subsystemForCategory(category),
        category,
        type,
        payload: redacted,
        route: opts.route ?? (typeof window !== "undefined" ? window.location.pathname : undefined),
        url: typeof window !== "undefined" ? window.location.href : undefined,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
        client_version: CLIENT_VERSION,
      };
      return event;
    },
    [identity],
  );

  const track = useCallback<ActivityContextValue["track"]>(
    (category, type, payload, opts) => {
      const event = buildEvent(category, type, payload, opts);
      if (!event) return;
      if (!identity) {
        pendingRef.current.push(event);
        return;
      }
      enqueue(event);
    },
    [buildEvent, enqueue, identity],
  );

  // Drain pre-auth backlog once identity resolves.
  useEffect(() => {
    if (!identity) return;
    if (pendingRef.current.length === 0) return;
    const drained = pendingRef.current.map((e) => ({
      ...e,
      user_id: identity.userId,
      org_id: identity.orgId,
    }));
    pendingRef.current = [];
    for (const e of drained) enqueue(e);
  }, [identity, enqueue]);

  // Wire the global error bridge so Sentry's beforeSend (which fires
  // before React mounts) can route JS exceptions into the activity
  // log via this provider.
  useEffect(() => {
    const listener = (e: ErrorBridgeEvent) => {
      track("error", "js.exception", {
        fingerprint: e.fingerprint,
        message: truncateText(e.message, 1_024),
      });
    };
    setErrorListener(listener);
    drainErrorBuffer();
    return () => setErrorListener(null);
  }, [track]);

  const value = useMemo<ActivityContextValue>(
    () => ({ track, sessionId: sessionIdRef.current }),
    [track],
  );

  return (
    <ActivityContext.Provider value={value}>
      {children}
    </ActivityContext.Provider>
  );
}

/** Author-facing hook. Returns `null` outside the provider. */
export function useActivity(): ActivityContextValue | null {
  return useContext(ActivityContext);
}

/**
 * Hardened variant — throws if called outside the provider. Use
 * for components that absolutely require activity capture.
 */
export function useActivityRequired(): ActivityContextValue {
  const v = useContext(ActivityContext);
  if (!v) throw new Error("useActivityRequired() called outside <UserActivityProvider>");
  return v;
}

/** Truncate-text helper re-exported for callers that pre-shape payloads. */
export { truncateText, PROMPT_MAX_CHARS };

function subsystemForCategory(category: ActivityEvent["category"]): string {
  // Subsystem is a flat label per master-plan §B (filtered in
  // LexPortal's subsystem dropdown). Categories that map cleanly
  // get their own subsystem; the rest fall back to "browser".
  switch (category) {
    case "navigation":
      return "navigation";
    case "interaction":
      return "interaction";
    case "form":
      return "form";
    case "prompt":
      return "chat";
    case "error":
      return "error";
    case "lifecycle":
      return "lifecycle";
    default:
      return "browser";
  }
}
